import React from 'react'
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Typography,
} from '@mui/material'
import { Photos, Videos, Panoramas, Description, WalkingPhotos } from '.'
import { useMediaDetails } from 'Hooks/useMediaDetails'
import { getWalkingPoints } from 'Hooks/getWalkingPoints'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'

interface SupplementalMediaProps {
  stopId?: number
  mainMediaId?: number
}

const SupplementalMedia = ({ stopId, mainMediaId }: SupplementalMediaProps) => {
  const {
    photos,
    photoAssetUrl,
    videos,
    panoramas,
    panoAssetUrl,
    photoHotspots,
    videoHotspots,
    panoHotspots,
    textHotspots,
    photoPosMedia,
    videoPosMedia,
    panoPosMedia,
  } = useMediaDetails(stopId)

  const { walkingPoints } = getWalkingPoints(stopId)

  const combinedPhotos =
    photos && photoHotspots && photoPosMedia
      ? [...photos, ...photoHotspots, ...photoPosMedia]
      : []
  const combinedVideos =
    videos && videoHotspots && videoPosMedia
      ? [...videos, ...videoHotspots, ...videoPosMedia]
      : []
  const combinedPanoramas =
    panoramas && panoHotspots && panoPosMedia
      ? [...panoramas, ...panoHotspots, ...panoPosMedia]
      : []

  const filteredPanoramas = mainMediaId
    ? combinedPanoramas.filter((item: any) => item.id !== mainMediaId)
    : combinedPanoramas

  const filteredPhotos = mainMediaId
    ? combinedPhotos.filter((item: any) => item.photoid !== mainMediaId)
    : combinedPhotos

  return (
    <>
      {filteredPhotos.length > 0 && (
        <Accordion slotProps={{ transition: { unmountOnExit: true } }}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography
              component='span'
              role='heading'
              aria-level={3}
              variant='subtitle1'
              sx={{ fontWeight: 600 }}
            >
              Photos
            </Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Photos photos={filteredPhotos} assetUrl={photoAssetUrl} />
          </AccordionDetails>
        </Accordion>
      )}

      {combinedVideos.length > 0 && (
        <Accordion slotProps={{ transition: { unmountOnExit: true } }}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography
              component='span'
              role='heading'
              aria-level={3}
              variant='subtitle1'
              sx={{ fontWeight: 600 }}
            >
              Videos
            </Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Videos videos={combinedVideos} />
          </AccordionDetails>
        </Accordion>
      )}

      {filteredPanoramas.length > 0 && (
        <Accordion slotProps={{ transition: { unmountOnExit: true } }}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography
              component='span'
              role='heading'
              aria-level={3}
              variant='subtitle1'
              sx={{ fontWeight: 600 }}
            >
              Panoramas
            </Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Panoramas panoramas={filteredPanoramas} assetUrl={panoAssetUrl} />
          </AccordionDetails>
        </Accordion>
      )}

      {textHotspots?.length > 0 && (
        <Accordion slotProps={{ transition: { unmountOnExit: true } }}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography
              component='span'
              role='heading'
              aria-level={3}
              variant='subtitle1'
              sx={{ fontWeight: 600 }}
            >
              Descriptions
            </Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Description hotspots={textHotspots} />
          </AccordionDetails>
        </Accordion>
      )}

      {walkingPoints.stopPoints.length > 0 && (
        <Accordion slotProps={{ transition: { unmountOnExit: true } }}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography
              component='span'
              role='heading'
              aria-level={3}
              variant='subtitle1'
              sx={{ fontWeight: 600 }}
            >
              Walking Points
            </Typography>
          </AccordionSummary>
          <AccordionDetails>
            <p>You are now walking to the next stop experience.</p>
            <WalkingPhotos
              walkingPoints={walkingPoints}
              assetUrl={photoAssetUrl}
            />
          </AccordionDetails>
        </Accordion>
      )}
    </>
  )
}

export default SupplementalMedia
